import React from 'react';
import { Tooltip, Box, Typography, Stack, Chip } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ITicket } from '../../Core/State/ITicket';
import { t } from '../../i18n';

interface UserTicketsTooltipProps {
    tickets?: ITicket[];
    children: React.ReactElement;
}

const UserTicketsTooltip: React.FC<UserTicketsTooltipProps> = ({ tickets, children }) => {
    const hasTickets = Array.isArray(tickets) && tickets.length > 0;

    if (!hasTickets) {
        return (
            <Tooltip
                title={
                    <Box sx={{
                        p: 1,
                        backgroundColor: '#232329',
                        borderRadius: '4px',
                        border: '1px solid #3A3B40',
                        fontFamily: 'Inter, sans-serif'
                    }}>
                        <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}>
                            {t('common:tickets.noTicketsAvailable')}
                        </Typography>
                    </Box>
                }
                placement="top"
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'transparent',
                            '& .MuiTooltip-arrow': {
                                color: '#3A3B40',
                            },
                        },
                    },
                }}
            >
                {children}
            </Tooltip>
        );
    }

    const sortKey = (ticket: ITicket) => {
        const usedKey = ticket.used ? 1 : 0; // unused first
        const expiredKey = ticket.expired ? 1 : 0; // non-expired first
        return usedKey * 10 + expiredKey; // stable grouping
    };
    const sorted = [...tickets!].sort((a, b) => sortKey(a) - sortKey(b));

    const titleNode = (
        <Box sx={{
            p: 1.2,
            backgroundColor: '#232329',
            borderRadius: '4px',
            border: '1px solid #3A3B40',
            minWidth: '280px',
            maxWidth: '360px',
            fontFamily: 'Inter, sans-serif'
        }}>
            <Stack spacing={0.8}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    pb: 0.6,
                    borderBottom: '1px solid #3A3B40'
                }}>
                    <ConfirmationNumberIcon sx={{ fontSize: '14px', color: '#2e6f6b' }} />
                    <Typography sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#2e6f6b',
                        letterSpacing: '0.4px',
                        fontFamily: 'Inter, sans-serif',
                        textTransform: 'uppercase'
                    }}>
                        {t('common:tickets.yourTickets', { count: sorted.length })}
                    </Typography>
                </Box>

                {/* Tickets List */}
                {sorted.map((ticket) => {
                    const name = ticket.ticketName || ticket.tournamentData?.name || ticket.tournamentId || 'Ticket';
                    const isUsed = Boolean(ticket.used);
                    const isExpired = Boolean(ticket.expired);

                    let statusColor = '#4caf50'; // Green for unused
                    let statusBgColor = 'rgba(76, 175, 80, 0.1)';
                    let statusBorderColor = 'rgba(76, 175, 80, 0.3)';
                    let statusIcon = <CheckCircleIcon sx={{ fontSize: '12px' }} />;
                    let statusLabel = t('common:tickets.statusAvailable');

                    if (isExpired) {
                        statusColor = '#ff6b6b';
                        statusBgColor = 'rgba(255, 107, 107, 0.1)';
                        statusBorderColor = 'rgba(255, 107, 107, 0.3)';
                        statusIcon = <CancelIcon sx={{ fontSize: '12px' }} />;
                        statusLabel = t('common:tickets.statusExpired');
                    } else if (isUsed) {
                        statusColor = '#9e9e9e';
                        statusBgColor = 'rgba(158, 158, 158, 0.1)';
                        statusBorderColor = 'rgba(158, 158, 158, 0.3)';
                        statusIcon = <CheckCircleIcon sx={{ fontSize: '12px' }} />;
                        statusLabel = t('common:tickets.statusUsed');
                    }

                    // Calculate days remaining
                    let daysRemaining = null;
                    if (!isExpired && !isUsed && ticket.expiryDate) {
                        const now = Date.now();
                        const until = Date.parse(ticket.expiryDate);
                        if (!Number.isNaN(until)) {
                            daysRemaining = Math.max(0, Math.ceil((until - now) / (1000 * 60 * 60 * 24)));
                        }
                    }

                    const eligibleNames = Array.isArray(ticket.allowedTournaments)
                        ? ticket.allowedTournaments.map((a) => a?.name).filter((n): n is string => Boolean(n))
                        : [];

                    return (
                        <Box
                            key={ticket._id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.6,
                                p: 0.8,
                                backgroundColor: statusBgColor,
                                borderRadius: '4px',
                                border: `1px solid ${statusBorderColor}`,
                                borderLeft: `3px solid ${statusColor}`
                            }}
                        >
                            {/* Ticket name and status */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    fontFamily: 'Inter, sans-serif',
                                    lineHeight: 1.3,
                                    flex: 1
                                }}>
                                    {name}
                                </Typography>
                                <Chip
                                    icon={statusIcon}
                                    label={statusLabel}
                                    size="small"
                                    sx={{
                                        height: '18px',
                                        fontSize: '9px',
                                        fontWeight: 700,
                                        backgroundColor: statusColor,
                                        color: '#fff',
                                        fontFamily: 'Inter, sans-serif',
                                        '& .MuiChip-label': { px: 0.6, py: 0 },
                                        '& .MuiChip-icon': { fontSize: '12px', ml: 0.4, color: '#fff' }
                                    }}
                                />
                            </Box>

                            {/* Days remaining */}
                            {daysRemaining !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                    <AccessTimeIcon sx={{ fontSize: '11px', color: '#19e5e0' }} />
                                    <Typography sx={{
                                        fontSize: '10px',
                                        color: '#19e5e0',
                                        fontWeight: 600,
                                        fontFamily: 'Inter, sans-serif'
                                    }}>
                                        {t('common:tickets.daysRemaining', { count: daysRemaining })}
                                    </Typography>
                                </Box>
                            )}

                            {/* Eligible tournaments */}
                            {eligibleNames.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.3,
                                    pt: 0.4,
                                    borderTop: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <Typography sx={{
                                        fontSize: '9px',
                                        color: 'rgba(255,255,255,0.5)',
                                        fontWeight: 600,
                                        fontFamily: 'Inter, sans-serif',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.3px'
                                    }}>
                                        {t('common:tickets.eligibleFor')}
                                    </Typography>
                                    {eligibleNames.map((n, idx) => (
                                        <Box
                                            key={`${ticket._id}-elig-${idx}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                ml: 0.5
                                            }}
                                        >
                                            <Box sx={{
                                                width: '4px',
                                                height: '4px',
                                                borderRadius: '50%',
                                                backgroundColor: '#2e6f6b',
                                                flexShrink: 0
                                            }} />
                                            <Typography sx={{
                                                fontSize: '10px',
                                                color: 'rgba(255,255,255,0.85)',
                                                fontFamily: 'Inter, sans-serif',
                                                lineHeight: 1.3
                                            }}>
                                                {n}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );

    return (
        <Tooltip
            title={titleNode}
            placement="top"
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'transparent',
                        '& .MuiTooltip-arrow': {
                            color: '#3A3B40',
                        },
                    },
                },
            }}
        >
            {children}
        </Tooltip>
    );
};

export default UserTicketsTooltip;


